import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Snackbar,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { initializeApp } from "firebase/app";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import { useDispatch, useSelector } from "react-redux";
import { addToCart, decrementQuantity } from "./CartReducer"; // Adjust path as necessary
import Headercomponent from "./Headercomponent"; // Import Header from the new file

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyDBnzIitbmRBp1o-RpA-uYLUDr3tWtdK1Y",
  authDomain: "lihen-pharmacy.firebaseapp.com",
  projectId: "lihen-pharmacy",
  storageBucket: "lihen-pharmacy.appspot.com",
  messagingSenderId: "546291727469",
  appId: "1:546291727469:web:3b1aaff3f11e0877e2d364",
  measurementId: "G-YXF7FF6CR9",
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

const ProductListWeb = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [originalProducts, setOriginalProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [deliverySnackbarOpen, setDeliverySnackbarOpen] = useState(false);
  const [deliveryMessage, setDeliveryMessage] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cart = useSelector((state) => state.cart.cart);

  useEffect(() => {
    fetchProductsFromFirestore();
    checkDeliveryTiming();
  }, []);

  const fetchProductsFromFirestore = async () => {
    try {
      setLoading(true);
      const productsCollection = collection(db, "Analgesics");
      const productsSnapshot = await getDocs(productsCollection);
      const productsData = productsSnapshot.docs.map((doc) => doc.data());
      setSearchResults(productsData);
      setOriginalProducts(productsData);
    } catch (error) {
      console.error("Error fetching products from Firestore:", error);
      alert("Error fetching products. Please check your network connection.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults(originalProducts);
    } else {
      const results = originalProducts.filter(
        (item) =>
          item.ID.toLowerCase().includes(query.toLowerCase()) ||
          item.description.toLowerCase().includes(query.toLowerCase())
      );
      setSearchResults(results);
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchResults(originalProducts);
  };

  const getItemCount = (product) => {
    if (!Array.isArray(cart)) return 0; // Ensure cart is always an array
    const item = cart.find((item) => item.ID === product.ID);
    return item ? item.quantity : 0;
  };

  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
    setTimeout(() => setSnackbarOpen(false), 3000);
  };

  const checkDeliveryTiming = () => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();

    // Check if the time is between 8:01 PM and 11:59 PM
    if ((hours === 20 && minutes >= 1) || (hours >= 21 && hours <= 23)) {
      setDeliveryMessage("Any order placed now will be delivered from 8am tomorrow.");
      setDeliverySnackbarOpen(true);
    }
    // Check if the time is between 12:00 AM and 7:59 AM
    else if ((hours >= 0 && hours < 8) || (hours === 8 && minutes < 59)) {
      setDeliveryMessage("Any order placed now will be delivered from 8am.");
      setDeliverySnackbarOpen(true);
    }
  };

  const isOutOfStock = (product) => {
    const unavailableValues = ["NO", "No", "N", "n", "no"];
    return unavailableValues.includes(product.available);
  };

  return (
    <Container>
      {/* Header */}
      <Headercomponent />

      {/* Delivery Timing Snackbar */}
      <Snackbar
        open={deliverySnackbarOpen}
        message={deliveryMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        autoHideDuration={6000}
        onClose={() => setDeliverySnackbarOpen(false)}
      />

      {/* Search Bar */}
      <TextField
        fullWidth
        placeholder="Search for products..."
        value={searchQuery}
        onChange={(e) => handleSearch(e.target.value)}
        variant="outlined"
        margin="normal"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: searchQuery && (
            <InputAdornment position="end">
              <IconButton onClick={clearSearch}>
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
        style={{
          borderRadius: "8px",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
          marginBottom: "20px",
        }}
      />

      {/* Product List */}
      {loading ? (
        <Grid container justifyContent="center" style={{ marginTop: "20px" }}>
          <CircularProgress />
        </Grid>
      ) : searchResults.length === 0 ? (
        <Alert severity="warning" style={{ marginTop: "20px" }}>
          No Results Found. Check Your Network and Refresh the Page.
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {searchResults.map((product) => (
            <Grid item xs={6} sm={6} md={4} key={product.ID}>
              <Card
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  alignItems: "center",
                  height: "100%",
                  cursor: isOutOfStock(product) ? "not-allowed" : "pointer",
                  opacity: isOutOfStock(product) ? 0.5 : 1,
                  borderRadius: "12px",
                  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                  padding: "10px",
                }}
                onClick={() =>
                  !isOutOfStock(product) &&
                  navigate(`/Productdetails/${product.ID}`, {
                    state: { product },
                  })
                }
              >
                <CardMedia
                  component="img"
                  image={product.image}
                  alt={product.description}
                  style={{
                    height: "150px",
                    width: "150px",
                    objectFit: "cover",
                    borderRadius: "12px",
                    margin: "10px auto",
                  }}
                />
                <CardContent style={{ textAlign: "center", padding: "10px" }}>
                  <Typography
                    variant="h6"
                    noWrap
                    style={{
                      fontSize: "0.9rem",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      maxWidth: "100%",
                      fontWeight: "bold",
                      color: "#4CAF50",
                    }}
                  >
                    {product.ID.length > 20
                      ? `${product.ID.slice(0, 20)}...`
                      : product.ID}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{
                      fontSize: "0.85rem",
                      display: "-webkit-box",
                      WebkitBoxOrient: "vertical",
                      WebkitLineClamp: 2,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {product.description.length > 50
                      ? `${product.description.slice(0, 50)}...`
                      : product.description}
                  </Typography>
                  <Typography variant="h6" color="primary">
                    ₦{parseInt(product.Price).toLocaleString()}
                  </Typography>
                  {isOutOfStock(product) ? (
                    <Typography
                      variant="body2"
                      color="error"
                      style={{ marginTop: "10px", fontWeight: "bold" }}
                    >
                      Out of Stock
                    </Typography>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginTop: "10px",
                        gap: "10px",
                      }}
                    >
                      {getItemCount(product) === 0 ? (
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            dispatch(addToCart(product));
                            showSnackbar(`${product.ID} is now 1 in the cart.`);
                          }}
                          style={{
                            width: "120px",
                            whiteSpace: "nowrap", // Ensure text stays on one line
                          }}
                        >
                          Add to Cart
                        </Button>
                      ) : (
                        <>
                          <Button
                            variant="contained"
                            color="secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              const currentCount = getItemCount(product) - 1;
                              dispatch(decrementQuantity(product));
                              showSnackbar(
                                `${product.ID} is now ${currentCount} in the cart.`
                              );
                            }}
                            style={{ minWidth: "40px" }}
                          >
                            -
                          </Button>
                          <Typography style={{ margin: "0 10px" }}>
                            {getItemCount(product)}
                          </Typography>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              const currentCount = getItemCount(product) + 1;
                              dispatch(addToCart(product));
                              showSnackbar(
                                `${product.ID} is now ${currentCount} in the cart.`
                              );
                            }}
                            style={{ minWidth: "40px" }}
                          >
                            +
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Snackbar for Cart Updates */}
      <Snackbar
        open={snackbarOpen}
        message={snackbarMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Container>
  );
};

export default ProductListWeb;